import React, { useEffect, useRef, useState } from 'react';
import { crm } from './crmApi';
import type { CrmAuth, CrmLead, CrmMe, Shift, Skill, Stage } from './crmApi';

// ── Shared pieces for the sales CRM pages ─────────────────────

export const STAGE_STYLE: Record<Stage, { bg: string; fg: string; dot: string }> = {
  'New':             { bg: '#eff6ff', fg: '#1d4ed8', dot: '#2563eb' },
  'Not Picked':      { bg: '#fef2f2', fg: '#b91c1c', dot: '#dc2626' },
  'Call Later':      { bg: '#fffbeb', fg: '#a16207', dot: '#d97706' },
  'Meeting Booked':  { bg: '#eef2ff', fg: '#4338ca', dot: '#4f46e5' },
  'P2P':             { bg: '#fef3c7', fg: '#92400e', dot: '#d97706' },
  'Poll Sent':       { bg: '#fdf4ff', fg: '#a21caf', dot: '#c026d3' },
  'Interested':      { bg: '#ecfdf5', fg: '#047857', dot: '#059669' },
  'No Response':     { bg: '#f1f5f9', fg: '#475569', dot: '#64748b' },
  'Cold Lead':       { bg: '#f3f4f6', fg: '#4b5563', dot: '#6b7280' },
  'Not Interested':  { bg: '#f3f4f6', fg: '#4b5563', dot: '#6b7280' },
  'Junk Lead':       { bg: '#fdf2f8', fg: '#9d174d', dot: '#be185d' },
  'Future Prospect': { bg: '#f0fdfa', fg: '#0f766e', dot: '#0d9488' },
  'Direct Visit':    { bg: '#fff7ed', fg: '#c2410c', dot: '#ea580c' },
  'Converted':       { bg: '#f0fdf4', fg: '#15803d', dot: '#16a34a' },
};

export const WAIT_LABEL: Record<string, string> = {
  'after-hours': 'Waiting for the next working morning',
  'nobody-free': 'Waiting: nobody free',
};
// Closed: nobody is working on them (Junk: nobody picked up after everyone's tries)
export const CLOSED_STAGES: string[] = ['Cold Lead', 'Not Interested', 'Junk Lead'];

export const StageChip: React.FC<{ stage: Stage; small?: boolean }> = ({ stage, small }) => {
  const s = STAGE_STYLE[stage] || STAGE_STYLE.New;
  return (
    <span className={`inline-flex items-center rounded-full font-bold whitespace-nowrap ${small ? 'text-[10px] px-2 py-0.5' : 'text-xs px-2.5 py-1'}`}
      style={{ background: s.bg, color: s.fg }}>
      {stage}
    </span>
  );
};

// WhatsApp lead (messaged directly) or form lead
export const LeadTypeChip: React.FC<{ type: string }> = ({ type }) => (
  type === 'WhatsApp' || type === 'Form' ? (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${type === 'WhatsApp' ? 'bg-green-100 text-green-800' : 'bg-sky-100 text-sky-800'}`}>
      {type === 'WhatsApp' ? 'WhatsApp lead' : 'Form lead'}
    </span>
  ) : null
);

// "Python · Strong" (the /p2p page form's skills); the stronger, the darker
const LEVEL_STYLE: Record<string, string> = { Strong: 'bg-purple-700 text-white', Medium: 'bg-purple-100 text-purple-800', Basic: 'bg-gray-100 text-gray-700' };
export const SkillChips: React.FC<{ skills?: Skill[] }> = ({ skills }) => (
  skills && skills.length ? (
    <div className="flex flex-wrap gap-1.5">
      {skills.map(s => (
        <span key={s.name} className={`text-xs font-semibold px-2.5 py-1 rounded-full ${LEVEL_STYLE[s.level] || 'bg-gray-100 text-gray-700'}`}>
          {s.name}{s.level ? <span className="font-normal opacity-80"> · {s.level}</span> : null}
        </span>
      ))}
    </div>
  ) : null
);

// '2:00 PM' from '14:00' (India time)
export const fmtHm = (hm: string) => {
  const [h, m] = hm.split(':').map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

// "Morning · 9:30 AM – 2:00 PM"; no shift: the whole working day
export const shiftTimes = (s: Shift) => `${fmtHm(s.start)} – ${fmtHm(s.end)}`;
export const shiftText = (s: Shift | null) => (s ? `${s.name} · ${shiftTimes(s)}` : 'Whole day');

// compact: the shift's name only (the times in its tooltip), for narrow lists
export const ShiftChip: React.FC<{ shift: Shift | null; onNow?: boolean; dark?: boolean; compact?: boolean }> = ({ shift, onNow, dark, compact }) => (
  <span title={shiftText(shift)} className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${dark ? 'bg-white/10 text-white border border-white/15' : shift?.key === 'B' ? 'bg-indigo-50 text-indigo-800' : shift ? 'bg-amber-50 text-amber-800' : 'bg-gray-100 text-gray-600'}`}>
    {onNow !== undefined && <span className={`w-1.5 h-1.5 rounded-full ${onNow ? 'bg-green-500' : 'bg-gray-400'}`} />}
    {compact ? (shift ? shift.name : 'Whole day') : shiftText(shift)}
  </span>
);

export const fmtPhone = (p: string) => (/^\d{10}$/.test(p) ? `${p.slice(0, 5)} ${p.slice(5)}` : p);

// Times are shown in India time, whatever the computer's clock says
const IST = 'Asia/Kolkata';
const dayKey = (d: Date) => d.toLocaleDateString('en-CA', { timeZone: IST });
export function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const time = d.toLocaleTimeString('en-IN', { timeZone: IST, hour: 'numeric', minute: '2-digit', hour12: true }).toUpperCase();
  const now = new Date();
  const tomorrow = new Date(now.getTime() + 86400000);
  if (dayKey(d) === dayKey(now)) return `Today ${time}`;
  if (dayKey(d) === dayKey(tomorrow)) return `Tomorrow ${time}`;
  return `${d.toLocaleDateString('en-IN', { timeZone: IST, weekday: 'short', day: 'numeric', month: 'short' })}, ${time}`;
}
export const fmtDay = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { timeZone: IST, weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '';

// "in 25 min" / "2 h late"
export function fmtRel(iso: string | null | undefined): string {
  if (!iso) return '';
  const mins = Math.round((new Date(iso).getTime() - Date.now()) / 60000);
  const span = (m: number) => (m < 60 ? `${m} min` : m < 48 * 60 ? `${Math.round(m / 60)} h` : `${Math.round(m / 1440)} days`);
  return mins >= 0 ? `in ${span(mins)}` : `${span(-mins)} late`;
}

// <input type="datetime-local"> holds India time: 'YYYY-MM-DDTHH:mm'
export const fromLocalInput = (v: string) => (v ? new Date(`${v}:00+05:30`).toISOString() : '');
export function toLocalInput(iso: string | Date): string {
  const d = new Date(new Date(iso).getTime() + 330 * 60000);
  return d.toISOString().slice(0, 16);
}

// Holidays (crmMe.holidays): the office is closed, so no follow-up goes on one
export const holidayOn = (me: CrmMe | null | undefined, local: string) => (local ? me?.holidays?.find(h => h.date === local.slice(0, 10)) || null : null);
export const holidayProblem = (me: CrmMe | null | undefined, local: string) => {
  const h = holidayOn(me, local);
  return h ? `${h.label} is a holiday (${h.name}). Pick another day.` : '';
};
export const HolidayWarning: React.FC<{ me: CrmMe; value: string }> = ({ me, value }) => {
  const h = holidayOn(me, value);
  return h ? <p className="text-xs font-bold text-red-700 bg-red-50 rounded-lg px-3 py-2 mt-2" role="alert">🏖️ {h.label} is a holiday ({h.name}): the office is closed. Pick another day.</p> : null;
};
// A suggested date and time moved off holidays and days off to the next working day, same time
export function offHolidays(me: CrmMe | null | undefined, local: string): string {
  const days = me?.work?.days;
  let v = local;
  for (let i = 0; i < 30; i++) {
    const weekday = new Date(`${v.slice(0, 10)}T00:00:00Z`).getUTCDay();
    if (!holidayOn(me, v) && (!days?.length || days.includes(weekday))) return v;
    v = `${new Date(Date.parse(`${v.slice(0, 10)}T00:00:00Z`) + 86400e3).toISOString().slice(0, 10)}${v.slice(10)}`;
  }
  return local;
}

export const inputCls = 'w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-purple-400 bg-white';
export const labelCls = 'block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5';

export const Spinner: React.FC<{ label?: string }> = ({ label }) => (
  <div className="flex flex-col items-center justify-center py-14 gap-3">
    <div className="w-8 h-8 rounded-full border-4 border-purple-200 border-t-purple-600 animate-spin" />
    {label && <p className="text-sm text-gray-400">{label}</p>}
  </div>
);

export const Empty: React.FC<{ icon: string; title: string; sub?: string }> = ({ icon, title, sub }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
    <div className="text-4xl mb-2">{icon}</div>
    <p className="font-bold text-gray-700">{title}</p>
    {sub && <p className="text-xs text-gray-400 mt-1.5">{sub}</p>}
  </div>
);

// Escape closes the top window when they are stacked (a lead, then Log call on it)
const openModals: Array<() => void> = [];
const onEscape = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && openModals.length) openModals[openModals.length - 1]();
};

export const Modal: React.FC<{ title: string; sub?: string; onClose: () => void; wide?: boolean; children: React.ReactNode }> = ({ title, sub, onClose, wide, children }) => {
  const closeRef = useRef(onClose);
  useEffect(() => { closeRef.current = onClose; });
  useEffect(() => {
    const close = () => closeRef.current();
    openModals.push(close);
    if (openModals.length === 1) window.addEventListener('keydown', onEscape);
    return () => {
      openModals.splice(openModals.indexOf(close), 1);
      if (!openModals.length) window.removeEventListener('keydown', onEscape);
    };
  }, []);
  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center p-3 sm:p-4" role="dialog" aria-modal="true" aria-label={title}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative bg-white rounded-2xl shadow-2xl w-full ${wide ? 'max-w-2xl' : 'max-w-lg'} max-h-[94vh] flex flex-col overflow-hidden`}>
        <div className="px-5 py-4 flex items-start justify-between gap-3 flex-shrink-0" style={{ background: 'linear-gradient(135deg,#2b2240,#4a3a6a)' }}>
          <div className="min-w-0">
            <p className="text-white font-bold text-base truncate">{title}</p>
            {sub && <p className="text-white/60 text-xs mt-0.5">{sub}</p>}
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white text-2xl leading-none" aria-label="Close">×</button>
        </div>
        <div className="overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
};

export const PageHead: React.FC<{ title: string; sub?: string; right?: React.ReactNode }> = ({ title, sub, right }) => (
  <div className="rounded-2xl px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
    style={{ background: 'linear-gradient(135deg,#2b2240 0%,#3d2f5f 60%,#2b2240 100%)' }}>
    <div>
      <h1 className="text-lg font-bold text-white">{title}</h1>
      {sub && <p className="text-white/60 text-xs mt-0.5">{sub}</p>}
    </div>
    {right && <div className="flex items-center gap-2 flex-wrap">{right}</div>}
  </div>
);

// A page's head: the dark band on its own page; inside a tabbed page (embedded)
// only the line under the tabs, so the title isn't shown twice
export const Head: React.FC<{ title: string; sub?: string; onRefresh?: () => void; embedded?: boolean }> = ({ title, sub, onRefresh, embedded }) => {
  if (!embedded) {
    return <PageHead title={title} sub={sub} right={onRefresh && <button onClick={onRefresh} className="px-3 py-2 rounded-xl text-xs font-semibold border border-white/20 text-white/80 hover:bg-white/10">↻ Refresh</button>} />;
  }
  return (
    <div className="flex items-center justify-between gap-3 px-1 flex-wrap">
      <p className="text-xs text-gray-500">{sub}</p>
      {onRefresh && <button onClick={onRefresh} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50">↻ Refresh</button>}
    </div>
  );
};

export const btnGold = 'px-4 py-2 rounded-xl text-xs font-bold text-white transition-all hover:opacity-90 disabled:opacity-50';
export const goldBg = { background: 'linear-gradient(135deg,#F0B429,#FF6535)' };
export const purpleBg = { background: 'linear-gradient(135deg,#2b2240,#5a4a6f)' };

// "Asha · 1 of 3 tries" — who has it and how many of their tries are used
// (older servers: "Asha · 2 of 3", how many salespeople it went to)
export function ownerLine(l: CrmLead, maxOwners: number): string {
  if (!l.ownerId) return l.stage === 'Junk Lead' ? 'Junk Leads (no one)' : WAIT_LABEL[l.waiting] || 'No one';
  if (l.tries != null) return `${l.ownerName} · ${l.tries} of ${l.triesEach ?? 3} tries`;
  const turn = l.stage === 'New' || l.stage === 'Not Picked' ? ` · ${Math.min(l.ownerCount, maxOwners)} of ${maxOwners}` : '';
  return `${l.ownerName}${turn}`;
}

// What to do next with a lead, in a few words: the list rows and the lead page
export type Tone = 'red' | 'amber' | 'purple' | 'green' | 'blue' | 'gray';
export const TONE: Record<Tone, string> = {
  red: 'text-red-600', amber: 'text-amber-700', purple: 'text-fuchsia-700', green: 'text-green-700', blue: 'text-blue-700', gray: 'text-gray-500',
};
const isDue = (iso: string | null) => !!iso && new Date(iso).getTime() <= Date.now() + 15 * 60000;

export function nextStep(l: CrmLead): { text: string; tone: Tone } {
  // A Student Coordinator handles it since the meeting (poll, payment)
  if (l.coordinatorName && !['Converted', ...CLOSED_STAGES].includes(l.stage)) return { text: `With ${l.coordinatorName} (coordinator)`, tone: 'blue' };
  switch (l.stage) {
    case 'Meeting Booked':  return meetingStep(l);
    case 'P2P':             return p2pStep(l);
    case 'Converted':       return { text: l.p2p?.outcome?.startsWith('Converted') ? `${l.p2p.outcome} ✓` : 'Paid ✓', tone: 'green' };
    case 'Cold Lead':       return { text: `Cold: ${l.reason || 'not a real lead'}`, tone: 'gray' };
    case 'Not Interested':  return { text: `Said no: ${l.reason || 'no reason given'}`, tone: 'gray' };
    case 'Junk Lead':       return { text: 'Junk: give it to someone to try again', tone: 'gray' };
    case 'Poll Sent':       return l.pollDue ? { text: 'Update the poll now', tone: 'red' } : { text: `Update poll by ${fmtWhen(l.nextAt)}`, tone: 'purple' };
    case 'Interested':      return l.paymentDue ? { text: 'Payment due: check it', tone: 'red' } : l.nextAt ? { text: `Pays ${fmtWhen(l.nextAt)}`, tone: 'green' } : { text: 'Waiting for payment', tone: 'green' };
    // (the reason: didn't come for the visit, or no response after the P2P meeting)
    case 'No Response':     return { text: l.reason || 'No reply to the poll', tone: 'gray' };
    case 'Direct Visit':    return l.visitDue ? { text: 'Did they come? Update it', tone: 'red' } : { text: `Visit ${fmtWhen(l.nextAt)}`, tone: 'amber' };
    case 'Future Prospect': return isDue(l.nextAt) ? { text: 'Call again now', tone: 'red' } : { text: `Call again ${fmtWhen(l.nextAt)}`, tone: 'blue' };
    case 'Call Later':      return isDue(l.nextAt) ? { text: 'Call back now', tone: 'red' } : { text: `Call back ${fmtWhen(l.nextAt)}`, tone: 'amber' };
    case 'Not Picked':      return !l.nextAt || isDue(l.nextAt) ? { text: 'Try again now', tone: 'red' } : { text: `Try again ${fmtWhen(l.nextAt)}`, tone: 'amber' };
    default:                return { text: 'Call them', tone: 'blue' };
  }
}

// A lead waiting for its coordinator meeting: what is next for the salesperson
function meetingStep(l: CrmLead): { text: string; tone: Tone } {
  const m = l.meeting;
  if (!m) return { text: 'Meeting booked', tone: 'blue' };
  if (m.status === 'Link Ready') return { text: 'Send the meeting link', tone: 'red' };
  if (m.status === 'Booked') return { text: `Meeting ${fmtWhen(m.at)} · ${m.coordinatorId ? 'waiting for the link' : 'no coordinator yet'}`, tone: 'amber' };
  return { text: `Meeting ${fmtWhen(m.at)}${m.coordinatorName ? ` with ${m.coordinatorName}` : ''}`, tone: 'blue' };
}

// A website form put them first in To call
export const webFormLabel = (f: { via: string; again: boolean }) => {
  const form = f.via === 'p2p' ? 'P2P' : 'Analyst';
  return f.again ? `🔁 Filled the ${form} form again` : `🎯 ${form} form`;
};

// A lead in Path to Placement: where its P2P case is (a Lead Manager handles it)
export const P2P_STEP_LABEL: Record<string, string> = {
  meeting: 'P2P meeting', fee: 'Waiting for the test fee', test: 'P2P test', pass: 'Passed the test', fail: 'Failed the test', ats: 'ATS resume to make', done: 'P2P over',
};
function p2pStep(l: CrmLead): { text: string; tone: Tone } {
  const p = l.p2p;
  if (!p || !p.step) return { text: 'In P2P', tone: 'amber' };
  if (p.step === 'meeting') return { text: `P2P meeting ${fmtWhen(p.meetingAt || null)} · ${p.lmName ? `with ${p.lmName}` : 'no Lead Manager yet'}`, tone: p.lmName ? 'blue' : 'amber' };
  // Once the test fee is paid, a Career Strategist has them
  const who = p.step === 'fee' ? p.lmName : p.csName;
  return { text: `${P2P_STEP_LABEL[p.step] || 'In P2P'}${who ? ` · ${who}` : ''}`, tone: 'amber' };
}

// How far a lead got: New → Called → Brochure → Poll → Interested → Paid
export const JOURNEY = ['New', 'Called', 'Brochure sent', 'Poll sent', 'Interested', 'Paid'];
export function journeyStep(l: CrmLead): number {
  if (l.stage === 'Converted') return 5;
  if (l.stage === 'Interested') return 4;
  if (l.stage === 'Poll Sent' || l.stage === 'No Response' || l.pollSentAt) return 3;
  if (l.pitched) return 2;
  if (l.callCount > 0) return 1;
  return 0;
}

export const Journey: React.FC<{ lead: CrmLead }> = ({ lead }) => {
  const at = journeyStep(lead);
  const stopped = CLOSED_STAGES.includes(lead.stage);
  return (
    <ol className="flex items-center gap-1 overflow-x-auto pb-1" aria-label={`Journey: ${JOURNEY[at]}${stopped ? `, then ${lead.stage}` : ''}`}>
      {JOURNEY.map((s, i) => {
        const done = i < at || (i === at && lead.stage === 'Converted');
        const here = i === at && lead.stage !== 'Converted';
        return (
          <li key={s} className="flex items-center gap-1 flex-shrink-0">
            <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${here ? (stopped ? 'bg-gray-700 text-white' : 'bg-purple-700 text-white') : done ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-400'}`}>
              {done ? '✓ ' : ''}{s}{here && stopped ? ` · ${lead.stage}` : ''}
            </span>
            {i < JOURNEY.length - 1 && <span className={`w-3 h-0.5 ${i < at ? 'bg-green-300' : 'bg-gray-200'}`} />}
          </li>
        );
      })}
    </ol>
  );
};

// Tabs inside a page (one page instead of many menu items)
export const Tabs: React.FC<{ tabs: { id: string; label: string; badge?: number }[]; value: string; onChange: (id: string) => void; label: string }> = ({ tabs, value, onChange, label }) => (
  <div className="flex gap-1 p-1 rounded-xl bg-white border border-gray-100 overflow-x-auto max-w-full w-fit" role="tablist" aria-label={label}>
    {tabs.map(t => (
      <button key={t.id} role="tab" aria-selected={value === t.id} onClick={() => onChange(t.id)}
        className="px-3.5 py-2 rounded-lg text-xs font-bold whitespace-nowrap flex items-center gap-1.5 transition-colors"
        style={value === t.id ? { background: '#2b2240', color: '#fff' } : { color: '#6b7280' }}>
        {t.label}
        {!!t.badge && <span className={`min-w-[18px] h-[18px] px-1 rounded-full text-[10px] leading-[18px] ${value === t.id ? 'bg-white/20' : 'bg-red-500 text-white'}`}>{t.badge}</span>}
      </button>
    ))}
  </div>
);

// One lead in a list: who, what stage, and what to do next
export const LeadRow: React.FC<{ lead: CrmLead; onOpen: () => void; showOwner?: boolean; maxOwners?: number; selected?: boolean; onSelect?: (on: boolean) => void; extra?: React.ReactNode; actions?: React.ReactNode }> = ({ lead: l, onOpen, showOwner, maxOwners = 3, selected, onSelect, extra, actions }) => {
  const next = nextStep(l);
  return (
    <div className={`bg-white border rounded-2xl transition-all ${selected ? 'border-purple-300 ring-2 ring-purple-100' : 'border-gray-100 hover:border-purple-200 hover:shadow-sm'}`}>
      <div className="flex items-stretch">
        {onSelect && (
          <label className="flex items-center pl-3 cursor-pointer">
            <input type="checkbox" checked={!!selected} onChange={e => onSelect(e.target.checked)} className="w-4 h-4 accent-purple-700" aria-label={`Select ${l.name || l.phone}`} />
          </label>
        )}
        <button onClick={onOpen} className="flex-1 min-w-0 text-left px-4 py-3 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-gray-800 truncate">{l.name || 'No name'}</span>
              <StageChip stage={l.stage} small />
              <LeadTypeChip type={l.leadType} />
              {l.webForm && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{webFormLabel(l.webForm)}</span>}
              {l.movedToYou && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">Moved to you</span>}
              {l.coordinatorName && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">🤝 {l.coordinatorName}</span>}
            </div>
            <p className="text-xs text-gray-500 mt-0.5 truncate">
              {fmtPhone(l.phone)}{l.course ? ` · ${l.course}` : ''}{l.source ? ` · ${l.source}` : ''}{l.mode ? ` · ${l.mode}` : ''}
            </p>
            {showOwner && <p className="text-[11px] text-gray-400 mt-0.5 truncate">#{l.leadNo} · {ownerLine(l, maxOwners)}</p>}
            {l.turnNote && <p className="text-[11px] font-semibold text-amber-700 mt-0.5">{l.turnNote}</p>}
            {extra}
          </div>
          <div className="text-right flex-shrink-0 max-w-[45%]">
            <p className={`text-xs font-bold ${TONE[next.tone]}`}>{next.text}</p>
            {l.nextAt && !['Converted', ...CLOSED_STAGES, 'No Response'].includes(l.stage) && <p className="text-[10px] text-gray-400">{fmtRel(l.nextAt)}</p>}
            {l.movesTomorrow && !l.turnNote && <p className="text-[10px] font-bold text-orange-600 mt-0.5">moves tomorrow</p>}
          </div>
        </button>
      </div>
      {actions && <div className="px-4 pb-3 -mt-1 flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
};

// The lists, profiles and people the forms offer (loaded once per sign-in)
const meCache = new Map<string, Promise<CrmMe>>();
export function useCrmMe(auth: CrmAuth) {
  const key = 'crmToken' in auth ? auth.crmToken : `admin:${auth.adminPassword}`;
  const [me, setMe] = useState<CrmMe | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    if (!meCache.has(key)) meCache.set(key, crm.me(auth));
    meCache.get(key)!.then(r => { if (live) setMe(r); }).catch(e => { meCache.delete(key); if (live) setError(e.message); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return { me, error };
}
// After the rules' lists change, the next screen reads them fresh
export const forgetCrmMe = () => meCache.clear();
